import { Fragment } from "react";
import type { DisbursementReport } from "@/services/financeService";
import { formatMoney } from "@/utils/format";
import { currencyLabel } from "@/utils/currency";

interface Props {
  report: DisbursementReport;
  schoolName: string;
  schoolYear: string;
  scopeLabel: string;
}

export default function DisbursementSheet({ report, schoolName, schoolYear, scopeLabel }: Props) {
  return (
    <div className="print-sheet w-full overflow-hidden rounded-lg border border-background-200 bg-white text-foreground-900">
      <div className="bg-primary-600 px-4 py-3 text-center">
        <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-background-50 md:text-base">
          Tableau prévisionnel des décaissements du {schoolName}
        </h2>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-x border-background-200 bg-background-100/70 px-4 py-2 text-xs">
        <span className="font-medium text-foreground-700">
          Session : <span className="text-foreground-950">{schoolYear}</span>
        </span>
        <span className="text-foreground-500">{scopeLabel}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse text-xs md:text-[13px]">
          <thead>
            <tr className="bg-background-200/80 text-left text-[11px] uppercase tracking-wide text-foreground-700">
              <th className="w-10 border border-background-300 px-2 py-2 text-center font-semibold">N°</th>
              <th className="border border-background-300 px-3 py-2 font-semibold">Charges prévisionnelles</th>
              <th className="w-28 border border-background-300 px-3 py-2 text-right font-semibold">Prévu mensuel</th>
              <th className="w-28 border border-background-300 px-3 py-2 text-right font-semibold">Prévu annuel</th>
              <th className="w-28 border border-background-300 px-3 py-2 text-right font-semibold">Réalisé</th>
              <th className="w-28 border border-background-300 px-3 py-2 text-right font-semibold">Écart</th>
            </tr>
          </thead>
          <tbody>
            {report.groups.map((group, groupIndex) => (
              <Fragment key={group.id}>
                <tr className="bg-secondary-100/70">
                  <td className="border border-background-300 px-2 py-2 text-center font-bold text-foreground-900">
                    {["I", "II", "III", "IV", "V", "VI", "VII", "VIII"][groupIndex] ?? groupIndex + 1}
                  </td>
                  <td
                    className="border border-background-300 px-3 py-2 font-bold uppercase tracking-wide text-foreground-900"
                    colSpan={5}
                  >
                    {group.title}
                  </td>
                </tr>
                {group.lines.map((line, index) => (
                  <tr key={`${group.id}-${line.label}`}>
                    <td className="border border-background-300 px-2 py-1.5 text-center text-foreground-500">{index + 1}</td>
                    <td className="border border-background-300 px-3 py-1.5 text-foreground-800">{line.label}</td>
                    <td className="border border-background-300 px-3 py-1.5 text-right tabular-nums text-foreground-700">
                      {formatMoney(line.monthly)}
                    </td>
                    <td className="border border-background-300 px-3 py-1.5 text-right tabular-nums text-foreground-800">
                      {formatMoney(line.annual)}
                    </td>
                    <td className="border border-background-300 px-3 py-1.5 text-right tabular-nums text-foreground-800">
                      {formatMoney(line.actual)}
                    </td>
                    <td
                      className={`border border-background-300 px-3 py-1.5 text-right tabular-nums font-medium ${
                        line.gap < 0 ? "text-accent-700" : "text-primary-700"
                      }`}
                    >
                      {formatMoney(line.gap)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-background-100">
                  <td className="border border-background-300 px-2 py-1.5"></td>
                  <td className="border border-background-300 px-3 py-1.5 font-semibold text-foreground-900">
                    Total {group.title}
                  </td>
                  <td className="border border-background-300 px-3 py-1.5 text-right font-semibold tabular-nums text-foreground-900">
                    {formatMoney(group.totalMonthly)}
                  </td>
                  <td className="border border-background-300 px-3 py-1.5 text-right font-semibold tabular-nums text-foreground-900">
                    {formatMoney(group.totalAnnual)}
                  </td>
                  <td className="border border-background-300 px-3 py-1.5 text-right font-semibold tabular-nums text-foreground-900">
                    {formatMoney(group.totalActual)}
                  </td>
                  <td
                    className={`border border-background-300 px-3 py-1.5 text-right font-semibold tabular-nums ${
                      group.totalGap < 0 ? "text-accent-700" : "text-foreground-900"
                    }`}
                  >
                    {formatMoney(group.totalGap)}
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-primary-100">
              <td className="border border-primary-300 px-2 py-2.5"></td>
              <td className="border border-primary-300 px-3 py-2.5 font-bold uppercase tracking-wide text-primary-900">
                Total des charges à décaisser
              </td>
              <td className="border border-primary-300 px-3 py-2.5 text-right font-bold tabular-nums text-primary-900">
                {formatMoney(report.grandMonthly)}
              </td>
              <td className="border border-primary-300 px-3 py-2.5 text-right font-bold tabular-nums text-primary-900">
                {formatMoney(report.grandAnnual)}
              </td>
              <td className="border border-primary-300 px-3 py-2.5 text-right font-bold tabular-nums text-primary-900">
                {formatMoney(report.grandActual)}
              </td>
              <td className="border border-primary-300 px-3 py-2.5 text-right font-bold tabular-nums text-primary-900">
                {formatMoney(report.grandGap)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-x border-b border-background-200 px-4 py-2.5 text-[11px] text-foreground-500">
        <span>Prévu annuel = prévisionnel mensuel × 12. Réalisé = dépenses effectivement enregistrées.</span>
        <span>Devise : {currencyLabel()}</span>
      </div>
    </div>
  );
}